import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import './Login.css';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleRoleRedirection = (userRole, subscribedDomains = [], accessibleModules = [], userMod = '') => {
        const normalizedRole = (userRole || 'user').toLowerCase();
        
        if (normalizedRole === 'superadmin') {
            navigate('/superadmin-dashboard');
            return;
        }

        if (normalizedRole === 'admin') {
            navigate('/admin-dashboard');
            return;
        }

        // Operational roles: Quality Manager, Supervisor, Shop Floor Operator, Operator, User
        const modules = (Array.isArray(accessibleModules) && accessibleModules.length > 0)
            ? accessibleModules
            : ((Array.isArray(subscribedDomains) && subscribedDomains.length > 0) ? subscribedDomains : (userMod ? [userMod] : []));

        if (modules.length > 1) {
            // Eligible for multiple domains -> Show Domain Selection Screen
            navigate('/select-module');
        } else if (modules.length === 1) {
            // Eligible for only 1 domain -> Direct Redirection
            const singleMod = modules[0];
            localStorage.setItem('userModule', singleMod);
            navigate('/dashboard');
        } else {
            setError('Access Restricted: Your organization does not have an active subscription for any domain.');
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        localStorage.clear();

        try {
            const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${serverUrl}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: username.trim(),
                    password
                })
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.message || 'Invalid username or password.');
                setIsLoading(false);
                return;
            }

            const role = (data.role || 'user').toLowerCase();
            const subscribedDomains = Array.isArray(data.subscribedDomains) ? data.subscribedDomains : [];
            const accessibleModules = Array.isArray(data.accessibleModules) ? data.accessibleModules : [];
            const userModule = data.module || (subscribedDomains[0] || '');

            localStorage.setItem('currentUser', data.username || username);
            localStorage.setItem('userRole', role);
            if (data.companyId) {
                localStorage.setItem('companyId', data.companyId);
            }
            if (data.companyName) {
                localStorage.setItem('companyName', data.companyName);
            }
            localStorage.setItem('subscribedDomains', JSON.stringify(subscribedDomains));
            localStorage.setItem('accessibleModules', JSON.stringify(accessibleModules));
            localStorage.setItem('userId', data._id || data.username || username);
            if (data.token) {
                localStorage.setItem('authToken', data.token);
            }

            handleRoleRedirection(role, subscribedDomains, accessibleModules, userModule);
        } catch (err) {
            console.log('Server unreachable, using client offline auth simulation');
            // Offline/fallback simulation for testing
            const cleanUser = username.trim().toLowerCase();
            let simulatedRole = 'user';
            let simulatedSubscribed = [];
            let simulatedAccessible = [];
            let simulatedModule = '';
            let simulatedCompanyName = '';
            let simulatedCompanyId = '60d5ec49f1a2c80015b6d1a1';

            if (cleanUser === 'superadmin') {
                simulatedRole = 'superadmin';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
            } else if (cleanUser === 'admin_cu') {
                simulatedRole = 'admin';
                simulatedSubscribed = ['CU'];
                simulatedAccessible = ['CU'];
                simulatedCompanyName = 'Federal Steel Industries';
                simulatedCompanyId = '60d5ec49f1a2c80015b6d1a1';
            } else if (cleanUser === 'admin_fe') {
                simulatedRole = 'admin';
                simulatedSubscribed = ['FE'];
                simulatedAccessible = ['FE'];
                simulatedModule = 'FE';
                simulatedCompanyName = 'Apex Alloys Manufacturing';
                simulatedCompanyId = '60d5ec49f1a2c80015b6d1a2';
            } else if (cleanUser === 'admin_multi' || cleanUser === 'admin') {
                simulatedRole = 'admin';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
                simulatedCompanyName = 'Federal Steel Industries';
                simulatedCompanyId = '60d5ec49f1a2c80015b6d1a1';
            } else if (cleanUser === 'qm' || cleanUser === 'quality_lead') {
                simulatedRole = 'Quality Manager';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
            } else if (cleanUser === 'sup' || cleanUser === 'supervisor') {
                simulatedRole = 'Supervisor';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
            } else if (cleanUser === 'operator' || cleanUser === 'shop_floor') {
                simulatedRole = 'Shop Floor Operator';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
            } else if (cleanUser === 'user_cu') {
                simulatedRole = 'user';
                simulatedSubscribed = ['CU'];
                simulatedAccessible = ['CU'];
                simulatedModule = 'CU';
            } else if (cleanUser === 'user_fe') {
                simulatedRole = 'user';
                simulatedSubscribed = ['FE'];
                simulatedAccessible = ['FE'];
                simulatedModule = 'FE';
            } else if (cleanUser === 'user_both' || cleanUser === 'user') {
                simulatedRole = 'user';
                simulatedSubscribed = ['CU', 'FE'];
                simulatedAccessible = ['CU', 'FE'];
            }

            if (password === '1234' || password.length > 0) {
                localStorage.setItem('currentUser', username);
                localStorage.setItem('userRole', simulatedRole);
                if (simulatedCompanyId) {
                    localStorage.setItem('companyId', simulatedCompanyId);
                }
                if (simulatedCompanyName) {
                    localStorage.setItem('companyName', simulatedCompanyName);
                }
                localStorage.setItem('subscribedDomains', JSON.stringify(simulatedSubscribed));
                localStorage.setItem('accessibleModules', JSON.stringify(simulatedAccessible));
                localStorage.setItem('userId', username);

                handleRoleRedirection(simulatedRole, simulatedSubscribed, simulatedAccessible, simulatedModule);
            } else {
                setError('Invalid credentials.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="login-box glass-panel"
            >
                <div className="login-header">
                    <div className="icon-container">
                        <img src="logo.png" alt="Logo" onError={(e) => { e.target.style.display = 'none'; }} />
                        {/* <ShieldCheck size={36} color="var(--primary-color)" /> */}
                    </div>
                    <h2 className="text-gradient">Ladle Logic - Charge Calculator</h2>
                    <p>Enter your credentials to sign in</p>
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    <div className="input-group">
                        <User className="input-icon" size={20} />
                        <input
                            type="text"
                            className="input-field with-icon"
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <Lock className="input-icon" size={20} />
                        <input
                            type="password"
                            className="input-field with-icon"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    {error && (
                        <motion.p
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="error-message"
                        >
                            {error}
                        </motion.p>
                    )}

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isLoading}
                        className="btn-primary w-full mt-4"
                    >
                        {isLoading ? 'Authenticating...' : 'Sign In'} <ArrowRight size={18} />
                    </motion.button>
                </form>
{/* 
                <div className="demo-accounts-hint mt-4 text-center" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '20px' }}>
                    <p style={{ fontWeight: 600, marginBottom: '4px' }}>Demo Credentials (Password: 1234):</p>
                    <p>Superadmin: <code>superadmin</code> | Admin: <code>admin_cu</code> / <code>admin_fe</code></p>
                    <p>User (Cu & Fe): <code>user_both</code> | User (Single): <code>user_cu</code></p>
                </div> */}
            </motion.div>
        </div>
    );
};

export default Login;
